// -----------------------------------------------------------------------------
//  TWO LANGUAGES, AND THE ONE DECISION THAT MATTERS
// -----------------------------------------------------------------------------
//
//  THE DEFAULT IS KHMER. Not English, and not the phone's locale.
//
//  The asymmetry decides it. An English-reading administrator who opens a Khmer
//  app can find the switch in one tap, because they can see it is a switch. A
//  Khmer-only field officer who opens an English app may not get as far as
//  finding it. Guessing wrong costs one of those two people a tap; it costs the
//  other the app.
//
//  THE SWITCH IS ON THE SIGN-IN SCREEN, before anyone has an account, and it
//  shows each language IN ITS OWN SCRIPT — "ខ្មែរ" and "English", never
//  "Language" or a translated label. A person who cannot read the current
//  language cannot navigate to a settings page to escape it, and cannot read a
//  label telling them what the button does. The names of the languages are the
//  only text on that button that works in both directions.
//
//  NO LIBRARY. i18next and friends are 40 kB and solve pluralisation rules for
//  Slavic languages. Khmer has no plural inflection and no grammatical gender;
//  a flat dictionary and a lookup are the whole requirement.
//
//  THE KHMER BELOW IS A DRAFT AND MUST BE REVIEWED BY A KHMER SPEAKER before
//  training. docs/10_khmer_review.md is the side-by-side list to hand them.
//  Wrong Khmer in a field app is worse than English, because an officer cannot
//  tell a translation error from an instruction they have misunderstood.
// -----------------------------------------------------------------------------

export type Lang = 'km' | 'en'

const KEY = 'vm.lang'

export const LANGUAGE_NAMES: Record<Lang, string> = {
  km: 'ខ្មែរ',
  en: 'English',
}

/** The dictionary. English first as the reference, Khmer as the draft. */
const STRINGS: Record<string, { en: string; km: string }> = {
  // ---- app-wide ------------------------------------------------------------
  'app.title':            { en: 'Village Mapping',      km: 'ការគូសផែនទីភូមិ' },
  'common.back':          { en: 'Back',                 km: 'ត្រឡប់' },
  'common.cancel':        { en: 'Cancel',               km: 'បោះបង់' },
  'common.save':          { en: 'Save',                 km: 'រក្សាទុក' },
  'common.saving':        { en: 'Saving…',              km: 'កំពុងរក្សាទុក…' },
  'common.loading':       { en: 'Loading…',             km: 'កំពុងផ្ទុក…' },
  'common.notRecorded':   { en: 'Not recorded',         km: 'មិនបានកត់ត្រា' },
  'common.yes':           { en: 'Yes',                  km: 'បាទ/ចាស' },
  'common.no':            { en: 'No',                   km: 'ទេ' },
  'common.unknown':       { en: 'Unknown',              km: 'មិនដឹង' },
  'common.people':        { en: 'people',               km: 'នាក់' },
  'common.signOut':       { en: 'Sign out',             km: 'ចាកចេញ' },
  'common.team':          { en: 'Team',                 km: 'ក្រុមការងារ' },

  // ---- signing in ----------------------------------------------------------
  'login.heading':        { en: 'Sign in',              km: 'ចូលប្រើប្រព័ន្ធ' },
  'login.email':          { en: 'Email',                km: 'អ៊ីមែល' },
  'login.password':       { en: 'Password',             km: 'ពាក្យសម្ងាត់' },
  'login.submit':         { en: 'Sign in',              km: 'ចូលប្រើ' },
  'login.working':        { en: 'Signing in…',          km: 'កំពុងចូល…' },
  'login.forgot':         { en: 'Forgotten your password?', km: 'ភ្លេចពាក្យសម្ងាត់?' },
  'login.noSignup':       { en: 'Accounts are created by your administrator. There is no self sign-up.',
                            km: 'គណនីត្រូវបានបង្កើតដោយអ្នកគ្រប់គ្រងប្រព័ន្ធ។ អ្នកមិនអាចចុះឈ្មោះដោយខ្លួនឯងបានទេ។' },
  'login.wrong':          { en: 'That email and password did not match. Check for a typo, or ask your administrator whether your account has been activated.',
                            km: 'អ៊ីមែល និងពាក្យសម្ងាត់មិនត្រូវគ្នាទេ។ សូមពិនិត្យមើលការវាយអក្សរ ឬសួរអ្នកគ្រប់គ្រងថាតើគណនីរបស់អ្នកត្រូវបានបើកដំណើរការហើយឬនៅ។' },

  // ---- villages ------------------------------------------------------------
  'villages.title':       { en: 'Villages',             km: 'ភូមិ' },
  'villages.signedInAs':  { en: 'Signed in as',         km: 'ចូលប្រើក្នុងនាម' },
  'villages.loading':     { en: 'Loading villages…',    km: 'កំពុងផ្ទុកភូមិ…' },
  'villages.tapToOpen':   { en: 'Tap to open →',        km: 'ចុចដើម្បីបើក →' },
  'villages.expected':    { en: 'households expected',  km: 'គ្រួសារដែលរំពឹងទុក' },
  'villages.registered':  { en: 'registered',           km: 'បានចុះបញ្ជី' },
  'villages.none':        { en: 'No villages are assigned to you. This is not an error — ask your supervisor to add you to a village.',
                            km: 'គ្មានភូមិណាត្រូវបានប្រគល់ឱ្យអ្នកទេ។ នេះមិនមែនជាកំហុសទេ — សូមស្នើអ្នកគ្រប់គ្រងឱ្យបន្ថែមអ្នកទៅភូមិមួយ។' },
  'villages.notActive':   { en: 'Your account exists but has not been activated yet. An administrator has to switch it on before you can see any data.',
                            km: 'គណនីរបស់អ្នកមាន ប៉ុន្តែមិនទាន់ត្រូវបានបើកដំណើរការទេ។ អ្នកគ្រប់គ្រងត្រូវបើកវាមុនពេលអ្នកអាចមើលឃើញទិន្នន័យ។' },

  // ---- one village ---------------------------------------------------------
  'village.add':          { en: '+ Add household',      km: '+ បន្ថែមគ្រួសារ' },
  'village.search':       { en: 'Search by name or number', km: 'ស្វែងរកតាមឈ្មោះ ឬលេខ' },
  'village.searchNone':   { en: 'No household matches that.', km: 'រកមិនឃើញគ្រួសារដែលត្រូវគ្នា។' },
  'village.showing':      { en: 'showing',              km: 'កំពុងបង្ហាញ' },
  'village.of':           { en: 'of',                   km: 'ក្នុងចំណោម' },
  'village.mapped':       { en: 'mapped',               km: 'បានគូសផែនទី' },
  'village.empty':        { en: 'No households registered yet.', km: 'មិនទាន់មានគ្រួសារចុះបញ្ជីទេ។' },
  'village.tapHousehold': { en: 'Tap to view or correct →', km: 'ចុចដើម្បីមើល ឬកែតម្រូវ →' },
  'village.noName':       { en: '(no name recorded)',   km: '(មិនបានកត់ត្រាឈ្មោះ)' },
  'village.noNumber':     { en: '(number pending)',     km: '(លេខកំពុងរង់ចាំ)' },
  'village.hasLatrine':   { en: 'has latrine',          km: 'មានបង្គន់' },
  'village.noLatrine':    { en: 'no latrine',           km: 'គ្មានបង្គន់' },
  'village.weakGps':      { en: 'weak GPS',             km: 'GPS ខ្សោយ' },
  'village.savedLocal':   { en: 'Saved on this phone. It will be sent when you have signal.',
                            km: 'បានរក្សាទុកក្នុងទូរស័ព្ទនេះ។ វានឹងផ្ញើនៅពេលមានសញ្ញា។' },

  // ---- the sync banner -----------------------------------------------------
  'sync.waiting':         { en: 'waiting to send',      km: 'រង់ចាំផ្ញើ' },
  'sync.needAttention':   { en: 'need attention',       km: 'ត្រូវការការពិនិត្យ' },
  'sync.savedTapToSend':  { en: 'Saved on this phone. Tap to send now.',
                            km: 'បានរក្សាទុកក្នុងទូរស័ព្ទ។ ចុចដើម្បីផ្ញើឥឡូវនេះ។' },
  'sync.willSendItself':  { en: 'No connection. It will send itself when you have signal.',
                            km: 'គ្មានអ៊ីនធឺណិត។ វានឹងផ្ញើដោយខ្លួនឯងនៅពេលមានសញ្ញា។' },
  'sync.offlineSafe':     { en: 'No connection · everything you save is kept on this phone',
                            km: 'គ្មានអ៊ីនធឺណិត · អ្វីៗដែលអ្នករក្សាទុកនៅតែមានក្នុងទូរស័ព្ទនេះ' },
  'sync.send':            { en: 'Send',                 km: 'ផ្ញើ' },
  'sync.sending':         { en: 'Sending…',             km: 'កំពុងផ្ញើ…' },
  'sync.tryAgain':        { en: 'Try again',            km: 'ព្យាយាមម្ដងទៀត' },
  'sync.allSent':         { en: 'Everything is uploaded.', km: 'បានផ្ញើអស់ហើយ។' },
  'sync.stillOffline':    { en: 'Still no connection. Your work is safe on this phone.',
                            km: 'នៅតែគ្មានអ៊ីនធឺណិត។ ការងាររបស់អ្នកនៅតែសុវត្ថិភាពក្នុងទូរស័ព្ទនេះ។' },
  'sync.signInAgain':     { en: 'Please sign in again to send.', km: 'សូមចូលប្រើម្ដងទៀតដើម្បីផ្ញើ។' },
  'sync.someRefused':     { en: 'Some records were refused — show this to your supervisor.',
                            km: 'កំណត់ត្រាខ្លះត្រូវបានបដិសេធ — សូមបង្ហាញនេះទៅអ្នកគ្រប់គ្រង។' },

  // ---- adding a household --------------------------------------------------
  'add.title':            { en: 'Add household',        km: 'បន្ថែមគ្រួសារ' },
  'add.step1':            { en: '1 · Location',         km: '១ · ទីតាំង' },
  'add.step2':            { en: '2 · Household',        km: '២ · គ្រួសារ' },
  'add.step3':            { en: '3 · Sanitation',       km: '៣ · អនាម័យ' },
  'add.standInFront':     { en: 'Stand in front of the house, in the open if you can, then capture. The app takes several readings and keeps the best one.',
                            km: 'ឈរនៅមុខផ្ទះ ក្នុងទីវាលបើអាច រួចចាប់យកទីតាំង។ កម្មវិធីនឹងអានច្រើនដង ហើយរក្សាទុកតម្លៃល្អបំផុត។' },
  'add.capture':          { en: 'Capture location',     km: 'ចាប់យកទីតាំង' },
  'add.captureAgain':     { en: 'Capture again',        km: 'ចាប់យកម្ដងទៀត' },
  'add.capturing':        { en: 'Capturing…',           km: 'កំពុងចាប់យក…' },
  'add.searching':        { en: 'Searching…',           km: 'កំពុងស្វែងរក…' },
  'add.readings':         { en: 'readings',             km: 'ការអាន' },
  'add.headName':         { en: 'Head of household name', km: 'ឈ្មោះមេគ្រួសារ' },
  'add.headSex':          { en: 'Sex of head of household', km: 'ភេទរបស់មេគ្រួសារ' },
  'add.hasLatrine':       { en: 'Has a latrine',        km: 'មានបង្គន់' },
  'add.latrineType':      { en: 'Latrine type',         km: 'ប្រភេទបង្គន់' },
  'add.waterSource':      { en: 'Main drinking water source', km: 'ប្រភពទឹកផឹកសំខាន់' },
  'add.householdSize':    { en: 'Household size',       km: 'ចំនួនសមាជិកគ្រួសារ' },
  'add.disabilityNote':   { en: 'People with a disability are counted separately and are already included in the groups above, so they are not added again.',
                            km: 'ជនពិការត្រូវបានរាប់ដោយឡែក ហើយបានរួមបញ្ចូលក្នុងក្រុមខាងលើរួចហើយ ដូច្នេះមិនត្រូវបូកបន្ថែមទៀតទេ។' },
  'add.submit':           { en: 'Save household',       km: 'រក្សាទុកគ្រួសារ' },
  'add.captureFirst':     { en: 'Capture the location before saving.', km: 'សូមចាប់យកទីតាំងមុនពេលរក្សាទុក។' },
  'add.dupBlocked':       { en: 'A household is already registered at this spot.',
                            km: 'មានគ្រួសារមួយបានចុះបញ្ជីនៅទីតាំងនេះរួចហើយ។' },
  'add.dupWarn':          { en: 'There is a household nearby. Is this the same house?',
                            km: 'មានគ្រួសារនៅជិត។ តើនេះជាផ្ទះតែមួយឬទេ?' },
  'add.dupBlockedHelp':   { en: 'Under 10 metres apart. If this really is a different house, move to its own doorway and capture the location again.',
                            km: 'ចម្ងាយតិចជាង ១០ ម៉ែត្រ។ បើនេះពិតជាផ្ទះផ្សេង សូមផ្លាស់ទៅមុខទ្វារផ្ទះនោះ ហើយចាប់យកទីតាំងម្ដងទៀត។' },
  'add.dupDifferent':     { en: 'This is a different house — continue',
                            km: 'នេះជាផ្ទះផ្សេង — បន្ត' },
  'add.metresAway':       { en: 'm away',               km: 'ម៉ែត្រ' },

  // ---- one household -------------------------------------------------------
  'hh.somethingChanged':  { en: 'Something needs changing?', km: 'មានអ្វីត្រូវកែប្រែឬ?' },
  'hh.correct':           { en: 'Correct a mistake',    km: 'កែតម្រូវកំហុស' },
  'hh.correctHelp':       { en: 'The name, the sex of the head, or the pin was recorded wrongly — or the house no longer exists. The wrong value is replaced, and the old one is kept in the history below.',
                            km: 'ឈ្មោះ ភេទមេគ្រួសារ ឬទីតាំងត្រូវបានកត់ត្រាខុស — ឬផ្ទះលែងមានទៀតហើយ។ តម្លៃខុសនឹងត្រូវជំនួស ហើយតម្លៃចាស់ត្រូវរក្សាទុកក្នុងប្រវត្តិខាងក្រោម។' },
  'hh.assess':            { en: 'Record what has changed', km: 'កត់ត្រាអ្វីដែលបានផ្លាស់ប្ដូរ' },
  'hh.assessHelp':        { en: 'The family built a latrine, someone was born, someone moved out. Both the old and the new answer are kept, so we can still say what the village looked like last year.',
                            km: 'គ្រួសារបានសាងសង់បង្គន់ មានកូនកើត ឬមានអ្នកចាកចេញ។ ចម្លើយចាស់ និងថ្មីត្រូវរក្សាទុកទាំងពីរ ដូច្នេះយើងនៅតែអាចដឹងពីស្ថានភាពភូមិកាលពីឆ្នាំមុន។' },
  'hh.history':           { en: 'change history',       km: 'ប្រវត្តិការផ្លាស់ប្ដូរ' },
  'hh.show':              { en: 'Show',                 km: 'បង្ហាញ' },
  'hh.hide':              { en: 'Hide',                 km: 'លាក់' },
  'hh.noHistory':         { en: 'Nothing has been corrected on this record since it was registered.',
                            km: 'មិនមានការកែតម្រូវលើកំណត់ត្រានេះទេ តាំងពីបានចុះបញ្ជីមក។' },
  'hh.historyOffline':    { en: 'The change history is kept on the server, so it is not available without a connection. Everything else on this screen works normally.',
                            km: 'ប្រវត្តិការផ្លាស់ប្ដូរត្រូវរក្សាទុកនៅលើម៉ាស៊ីនមេ ដូច្នេះវាមិនអាចមើលបានទេបើគ្មានអ៊ីនធឺណិត។ អ្វីៗផ្សេងទៀតនៅលើអេក្រង់នេះដំណើរការធម្មតា។' },
  'hh.lastAssessed':      { en: 'Last assessed',        km: 'វាយតម្លៃចុងក្រោយ' },
  'hh.never':             { en: 'never',                km: 'មិនធ្លាប់' },

  // ---- correcting ----------------------------------------------------------
  'fix.title':            { en: 'Correct',              km: 'កែតម្រូវ' },
  'fix.onlyMistakes':     { en: 'Use this only when something was recorded wrongly. If the household has genuinely changed — a new latrine, a new baby — go back and choose "Record what has changed" instead, so the old answer is not lost.',
                            km: 'ប្រើវាតែពេលមានអ្វីកត់ត្រាខុសប៉ុណ្ណោះ។ បើគ្រួសារពិតជាបានផ្លាស់ប្ដូរ — មានបង្គន់ថ្មី ឬកូនថ្មី — សូមត្រឡប់ក្រោយ ហើយជ្រើសរើស "កត់ត្រាអ្វីដែលបានផ្លាស់ប្ដូរ" ជំនួសវិញ ដើម្បីកុំឱ្យបាត់ចម្លើយចាស់។' },
  'fix.whoLives':         { en: 'Who lives here',       km: 'អ្នកណារស់នៅទីនេះ' },
  'fix.stillValid':       { en: 'Is this record still valid?', km: 'តើកំណត់ត្រានេះនៅត្រឹមត្រូវទេ?' },
  'fix.neverDeleted':     { en: 'A household is never deleted. Marking it inactive keeps its history and takes it out of the counts.',
                            km: 'គ្រួសារមិនត្រូវលុបចោលឡើយ។ ការសម្គាល់ថាមិនសកម្មរក្សាប្រវត្តិរបស់វា ហើយដកចេញពីការរាប់។' },
  'fix.active':           { en: 'Active',               km: 'សកម្ម' },
  'fix.activeWhy':        { en: 'The household is here and should be counted.',
                            km: 'គ្រួសារនៅទីនេះ ហើយគួររាប់បញ្ចូល។' },
  'fix.inactive':         { en: 'Not here any more',    km: 'លែងនៅទីនេះទៀត' },
  'fix.inactiveWhy':      { en: 'The house was demolished, or the family left the village.',
                            km: 'ផ្ទះត្រូវបានរុះរើ ឬគ្រួសារបានចាកចេញពីភូមិ។' },
  'fix.flagged':          { en: 'Needs checking',       km: 'ត្រូវការពិនិត្យ' },
  'fix.flaggedWhy':       { en: 'Something is wrong and a supervisor should look.',
                            km: 'មានអ្វីមិនប្រក្រតី ហើយអ្នកគ្រប់គ្រងគួរពិនិត្យ។' },
  'fix.whyRequired':      { en: 'Why? (required)',      km: 'ហេតុអ្វី? (ត្រូវបំពេញ)' },
  'fix.pinWrong':         { en: 'Is the pin on the wrong house?', km: 'តើទីតាំងស្ថិតលើផ្ទះខុសឬ?' },
  'fix.onlyIfHere':       { en: 'Only move it if you are standing at this house now. Moving it from somewhere else makes the map worse, not better.',
                            km: 'ផ្លាស់ប្ដូរវាតែពេលអ្នកកំពុងឈរនៅផ្ទះនេះឥឡូវនេះ។ ការផ្លាស់ប្ដូរពីកន្លែងផ្សេងធ្វើឱ្យផែនទីកាន់តែខុស។' },
  'fix.captureHere':      { en: 'Capture location here', km: 'ចាប់យកទីតាំងនៅទីនេះ' },
  'fix.submit':           { en: 'Save correction',      km: 'រក្សាទុកការកែតម្រូវ' },
  'fix.nothingChanged':   { en: 'Nothing has been changed yet.', km: 'មិនទាន់មានការផ្លាស់ប្ដូរទេ។' },
  'fix.sayWhy':           { en: 'Say why the status is changing.', km: 'សូមប្រាប់ហេតុផលនៃការផ្លាស់ប្ដូរស្ថានភាព។' },

  // ---- reassessment --------------------------------------------------------
  'up.title':             { en: 'Update',               km: 'ធ្វើបច្ចុប្បន្នភាព' },
  'up.onlyDifferent':     { en: 'Change only what is different today. Everything you leave alone keeps its earlier date, so we can still say what this household looked like last year.',
                            km: 'ផ្លាស់ប្ដូរតែអ្វីដែលខុសពីមុននៅថ្ងៃនេះ។ អ្វីដែលអ្នកមិនប៉ះនឹងរក្សាកាលបរិច្ឆេទចាស់ ដូច្នេះយើងនៅតែដឹងពីស្ថានភាពគ្រួសារនេះកាលពីឆ្នាំមុន។' },
  'up.people':            { en: 'People',               km: 'មនុស្ស' },
  'up.sanitation':        { en: 'Sanitation and water', km: 'អនាម័យ និងទឹក' },
  'up.visitNote':         { en: 'Note about this visit (optional)', km: 'កំណត់សម្គាល់អំពីការចុះនេះ (មិនចាំបាច់)' },
  'up.nothingYet':        { en: 'Nothing is different yet. Change a value above, or cancel.',
                            km: 'មិនទាន់មានអ្វីខុសគ្នាទេ។ សូមផ្លាស់ប្ដូរតម្លៃខាងលើ ឬបោះបង់។' },
  'up.was':               { en: 'was',                  km: 'ពីមុន' },

  // ---- the report and the export -------------------------------------------
  'village.report':       { en: 'View summary',         km: 'មើលរបាយការណ៍សង្ខេប' },
  'report.title':         { en: 'Summary',              km: 'របាយការណ៍សង្ខេប' },
  'report.asOf':          { en: 'Figures as of',        km: 'តួលេខគិតត្រឹម' },
  'report.asOfOffline':   { en: 'Last figures from this phone, as of',
                            km: 'តួលេខចុងក្រោយពីទូរស័ព្ទនេះ គិតត្រឹម' },
  'report.ofRegistered':  { en: 'of households registered', km: 'នៃគ្រួសារដែលបានចុះបញ្ជី' },
  'report.section.coverage':   { en: 'Coverage',        km: 'វិសាលភាព' },
  'report.section.people':     { en: 'Who lives here',  km: 'អ្នករស់នៅ' },
  'report.section.sanitation': { en: 'Sanitation',      km: 'អនាម័យ' },
  'report.section.water':      { en: 'Drinking water',  km: 'ទឹកផឹក' },
  'report.section.quality':    { en: 'Needs attention', km: 'ត្រូវការការយកចិត្តទុកដាក់' },
  'report.exportHeading': { en: 'Download the household list', km: 'ទាញយកបញ្ជីគ្រួសារ' },
  'report.exportCsv':     { en: 'Download CSV',         km: 'ទាញយកឯកសារ CSV' },
  'report.exporting':     { en: 'Preparing the file…',  km: 'កំពុងរៀបចំឯកសារ…' },
  'report.exportDone':    { en: 'households downloaded.', km: 'គ្រួសារត្រូវបានទាញយក។' },
  'report.exportNone':    { en: 'No households were returned. Either you are not assigned to this village, or your role does not allow downloading the household list.',
                            km: 'គ្មានគ្រួសារត្រូវបានទាញយកទេ។ អ្នកមិនត្រូវបានប្រគល់ភូមិនេះ ឬតួនាទីរបស់អ្នកមិនអនុញ្ញាតឱ្យទាញយកបញ្ជីគ្រួសារ។' },
  'report.exportWarning': { en: 'This file contains household names. Keep it inside your organisation, and do not attach it to a report that goes outside.',
                            km: 'ឯកសារនេះមានឈ្មោះគ្រួសារ។ សូមរក្សាទុកក្នុងអង្គភាពរបស់អ្នក ហើយកុំភ្ជាប់ទៅរបាយការណ៍ដែលចេញទៅខាងក្រៅ។' },
  'report.exportDenied':  { en: 'Downloading the household list is for supervisors and MERL officers. You can still open any household in this village one at a time. Ask your supervisor if you need the file.',
                            km: 'ការទាញយកបញ្ជីគ្រួសារគឺសម្រាប់អ្នកគ្រប់គ្រង និងមន្ត្រី MERL។ អ្នកនៅតែអាចបើកមើលគ្រួសារនីមួយៗក្នុងភូមិនេះបាន។ សូមស្នើអ្នកគ្រប់គ្រងប្រសិនបើអ្នកត្រូវការឯកសារនេះ។' },
  'report.shareNote':     { en: 'The numbers on this screen contain no names and no locations. They are safe to photograph, print, or send to a partner.',
                            km: 'តួលេខនៅលើអេក្រង់នេះមិនមានឈ្មោះ និងទីតាំងទេ។ វាមានសុវត្ថិភាពក្នុងការថតរូប បោះពុម្ព ឬផ្ញើទៅដៃគូ។' },
}

// -----------------------------------------------------------------------------

let current: Lang = read()

function read(): Lang {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'en' || v === 'km') return v
  } catch {
    // A private window, or storage switched off. Fall through to the default.
  }
  return 'km'
}

export function getLang(): Lang {
  return current
}

const listeners = new Set<() => void>()

export function setLang(l: Lang) {
  current = l
  try {
    localStorage.setItem(KEY, l)
  } catch {
    // Not being able to remember the choice is survivable; refusing to apply
    // it would not be.
  }
  // Screen readers and the browser's own line-breaking need to know.
  document.documentElement.lang = l
  for (const fn of listeners) fn()
}

export function onLangChange(fn: () => void): () => void {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

/**
 * Look up a string. An unknown key returns the key itself rather than an empty
 * string — a visible `village.searchX` on screen is a bug report; a blank space
 * is a mystery.
 */
export function t(key: string): string {
  const row = STRINGS[key]
  if (!row) return key
  return row[current] || row.en
}

/** Every key, for the review document and the tests. */
export function allKeys(): string[] {
  return Object.keys(STRINGS)
}

export function entry(key: string): { en: string; km: string } | undefined {
  return STRINGS[key]
}

// -----------------------------------------------------------------------------
//  MAKING A LANGUAGE CHANGE ACTUALLY REDRAW THE APP
// -----------------------------------------------------------------------------
//  t() is a plain function call, so React has no idea a screen depends on the
//  current language. Without this, tapping "English" re-rendered the switch and
//  left every other word on the page in Khmer — which looks exactly like the
//  switch is broken.
//
//  Caught by the phone smoke test, not by reading the code: the choice was
//  being stored correctly and appeared to work after a reload, so only an
//  actual tap-then-look revealed it.
//
//  One subscription at the root of the tree is enough. Re-rendering the whole
//  app on a language change is fine — it happens once, deliberately, by hand.
// -----------------------------------------------------------------------------

export function subscribeLang(onChange: () => void): () => void {
  return onLangChange(onChange)
}
